import { useState } from "react";
import { Check, Mail, MapPin, Phone } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { WorkAreaSection } from "../components/WorkAreaSection";
import { QuoteForm } from "../components/QuoteForm";
import { FadeUp, ButtonPrimary } from "../components/Reveal";
import { useContent } from "../context/ContentContext";

export default function Contact() {
  const [sent, setSent] = useState(false);
  const { site, pages } = useContent();

  return (
    <main data-testid="contact-page">
      <PageHero
        eyebrow="Contact & offerte"
        title={pages.contact.title}
        intro={pages.contact.intro}
      />

      <section className="bg-white">
        <div className="mx-auto grid max-w-[1400px] gap-10 px-4 pb-14 sm:px-6 lg:grid-cols-3">
          <div>
            <FadeUp>
              <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-[#111111]">
                Direct contact
              </h2>
            </FadeUp>
            <div className="mt-6">
              {[
                {
                  icon: Phone,
                  label: "Telefoon",
                  value: site.phoneDisplay,
                  href: site.phoneHref,
                  testid: "contact-info-phone",
                },
                {
                  icon: Mail,
                  label: "E-mail",
                  value: site.email,
                  href: `mailto:${site.email}`,
                  testid: "contact-info-email",
                },
                {
                  icon: MapPin,
                  label: "Werkgebied",
                  value: site.workArea,
                  href: null,
                  testid: "contact-info-location",
                },
              ].map(({ icon: Icon, label, value, href, testid }, i) => (
                <FadeUp key={label} delay={i * 0.05}>
                  <div data-testid={testid} className="flex items-start gap-3 border-b border-[#E2E0D9] py-4">
                    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#8E8D8A]" />
                    <div>
                      <p className="font-display text-xs font-bold uppercase tracking-[0.15em] text-[#8E8D8A]">
                        {label}
                      </p>
                      {href ? (
                        <a
                          href={href}
                          className="mt-1 block text-[15px] text-[#111111] hover:underline"
                        >
                          {value}
                        </a>
                      ) : (
                        <p className="mt-1 text-[15px] text-[#111111]">{value}</p>
                      )}
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
            <FadeUp delay={0.2}>
              <p className="mt-6 border border-[#DDD9D0] bg-[#EFEDE8] p-5 text-sm leading-relaxed text-[#555555]">
                Bellen kan altijd. Spreek gerust een voicemail in of stuur
                een bericht. Ik bel je terug zodra ik van de steiger af ben.
              </p>
            </FadeUp>
          </div>

          <div className="lg:col-span-2">
            {sent ? (
              <FadeUp>
                <div
                  data-testid="contact-form-success"
                  className="border border-[#DDD9D0] bg-[#EFEDE8] p-8 sm:p-12"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-[4px] bg-cream text-[#111111]">
                    <Check className="h-6 w-6" />
                  </span>
                  <h2 className="mt-6 font-display text-2xl font-bold uppercase tracking-wide text-[#111111] sm:text-3xl">
                    Bedankt voor je aanvraag
                  </h2>
                  <p className="mt-3 max-w-md text-[15px] leading-relaxed text-[#555555]">
                    Je aanvraag is goed ontvangen. Ik neem zo snel mogelijk
                    contact met je op om je project te bespreken. Spoed? Bel
                    me direct op {site.phoneDisplay}.
                  </p>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <ButtonPrimary to="/portfolio" testid="contact-success-portfolio-link">
                      Bekijk portfolio
                    </ButtonPrimary>
                    <button
                      type="button"
                      data-testid="contact-form-again-button"
                      onClick={() => setSent(false)}
                      className="rounded-[4px] border border-[#111111] px-6 py-3 font-display text-sm font-bold uppercase tracking-wide text-[#111111] transition-colors duration-200 hover:border-cream hover:bg-cream"
                    >
                      Nog een aanvraag
                    </button>
                  </div>
                </div>
              </FadeUp>
            ) : (
              <FadeUp delay={0.05}>
                <QuoteForm onSent={() => setSent(true)} />
              </FadeUp>
            )}
          </div>
        </div>
      </section>
      <WorkAreaSection />
    </main>
  );
}
